/**
 * Keeps a slow, repeatedly-requested read (e.g. the outstanding call-ins report, 1-2 s) from piling up:
 *
 * - a request for the same key while one is already in flight shares that request instead of firing another;
 * - when requests for different keys overlap, only the newest one's result is applied, so a slow older
 *   response can never overwrite a newer one.
 */
export class RequestCoalescer<T> {
    private inFlight: {key: string; promise: Promise<T>} | null = null;
    private latest = 0;

    run(key: string, fetch: () => Promise<T>, apply: (result: T) => void): Promise<T> {
        if (this.inFlight && this.inFlight.key === key) {
            return this.inFlight.promise;
        }

        const seq = ++this.latest;
        const promise = fetch()
            .then((result) => {
                if (seq === this.latest) {
                    apply(result);
                }
                return result;
            })
            .finally(() => {
                if (this.inFlight && this.inFlight.promise === promise) {
                    this.inFlight = null;
                }
            });
        this.inFlight = {key, promise};
        return promise;
    }
}
